import { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { sendNewPostNotification } from "../helpers/Api";

export default function CreatePostPopup({ onClose }) {
  const [title, setTitle] = useState("");
  const [textContent, setTextContent] = useState("");
  const [linkContent, setLinkContent] = useState("");
  const [mediaContent, setMediaContent] = useState([]);
  const [loading, setLoading] = useState(false);

  const [postId, setPostId] = useState(null); // id của draft post

  // --- Generate thumbnail từ video ---
  const generateVideoThumbnail = (fileOrUrl, callback) => {
    const video = document.createElement("video");

    const isStringUrl = typeof fileOrUrl === "string";

    // If we receive a File/Blob, create an object URL; if it's a string URL, use it directly.
    let objectUrl = null;
    if (isStringUrl) {
      console.log("Using URL directly for video source.");
      video.src = fileOrUrl;
      video.crossOrigin = "anonymous";
    } else {
      console.log("Creating object URL for video file/blob.");
      objectUrl = URL.createObjectURL(fileOrUrl);
      video.src = objectUrl;
    }

    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";

    // ensure we only generate once (prevent multiple event handlers from producing duplicates)
    let generated = false;

    const cleanup = () => {
      try {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      } catch (e) {
        // ignore
      }
      try {
        video.removeAttribute("src");
        video.load && video.load();
      } catch (e) {
        // ignore
      }
    };

    const handleError = (e) => {
      if (generated) return;
      generated = true;
      console.error("Video load error:", e);
      cleanup();
    };

    const generate = () => {
      if (generated) return;
      // ensure video has dimensions before drawing
      if (!video.videoWidth || !video.videoHeight) {
        setTimeout(generate, 150);
        return;
      }
      generated = true;
      try {
        const canvas = document.createElement("canvas");
        const vw = video.videoWidth || 320;
        const vh = video.videoHeight || 180;
        canvas.width = vw;
        canvas.height = vh;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            try {
              if (blob) callback(blob);
            } finally {
              cleanup();
            }
          },
          "image/jpeg",
          0.92
        );
      } catch (err) {
        console.error("Error generating video thumbnail:", err);
        cleanup();
      }
    };

    // When metadata is loaded, seek a little into the video to avoid black first frames
    const onLoadedMetadata = () => {
      const duration = isNaN(video.duration) ? 0 : video.duration;
      const seekTo = duration > 0 ? Math.min(0.1, duration / 2) : 0.1;
      try {
        // Some browsers throw if currentTime is set too early; wrap in try/catch
        video.currentTime = seekTo;
      } catch (e) {
        // fallback: generate from current frame after a small delay
        setTimeout(generate, 200);
      }
    };

    // After seeking, wait a tiny bit to ensure the frame is available, then draw
    const onSeeked = () => {
      if (generated) return;
      setTimeout(() => {
        generate();
      }, 80);
    };

    // Fallback: if loadeddata fires but seeked didn't, draw after a short delay
    const onLoadedDataFallback = () => {
      if (generated) return;
      setTimeout(() => {
        if (generated) return;
        if (!video.videoWidth || !video.videoHeight) {
          // still not ready, give one more short chance
          setTimeout(generate, 200);
        } else {
          generate();
        }
      }, 200);
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
    video.addEventListener("seeked", onSeeked, { once: true });
    video.addEventListener("loadeddata", onLoadedDataFallback, { once: true });
    video.addEventListener("error", handleError, { once: true });

    // start loading
    try {
      video.load();
    } catch (e) {
      // ignore
    }
  };

  const uploadToStorage = async (path, file) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  // --- tạo draft khi có title ---
  useEffect(() => {
    // tạo draft sau khi người dùng ngừng gõ title (debounce)
    if (!title.trim() || postId) return;

    const handler = setTimeout(async () => {
      try {
        const docRef = await addDoc(collection(db, "posts"), {
          title,
          textContent: "",
          linkContent: "",
          mediaContent: [],
          createdDate: Date.now(),
          editedDate: Date.now(),
          disabled: false,
          type: 0,
          isDraft: true, // đánh dấu draft
        });
        setPostId(docRef.id);
      } catch (err) {
        console.error("Lỗi tạo draft:", err);
      }
    }, 800); // chờ 800ms sau lần gõ cuối cùng

    return () => clearTimeout(handler);
  }, [title, postId]);

  const addMediaFromUrl = async (url, isVideo = false) => {
    console.log('Adding media from URL:', url, 'isVideo:', isVideo);
    if (!postId) {
      alert("Hãy nhập tiêu đề trước khi thêm media!");
      return;
    }

    setLoading(true);
    try {
      let thumbUrl = url;
      let filePath = `posts/${postId}/${Date.now()}`;
      let mediaItem = { isVideo, url, thumbUrl, filePath, thumbPath: null };

      if (isVideo) {
        generateVideoThumbnail(url, async (thumbBlob) => {
          const thumbPath = `posts/${postId}/thumb_${Date.now()}.jpg`;
          thumbUrl = await uploadToStorage(thumbPath, thumbBlob);
          mediaItem.thumbUrl = thumbUrl;
          mediaItem.thumbPath = thumbPath;
          setMediaContent((prev) => [...prev, mediaItem]);
          setLoading(false);
        });
      } else {
        const img = document.createElement("img");
        img.src = url;
        img.crossOrigin = "anonymous";
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const maxSize = 720;
          let width = img.width;
          let height = img.height;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(async (blob) => {
            const thumbPath = `posts/${postId}/thumb_${Date.now()}.jpg`;
            thumbUrl = await uploadToStorage(thumbPath, blob);
            mediaItem.thumbUrl = thumbUrl;
            mediaItem.thumbPath = thumbPath;
            setMediaContent((prev) => [...prev, mediaItem]);
            setLoading(false);
          }, "image/jpeg", 0.8);
        };
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, isVideo = false) => {
    if (!postId) {
      alert("Hãy nhập tiêu đề trước khi upload!");
      return;
    }

    setLoading(true);
    try {
      const filePath = `posts/${postId}/${Date.now()}_${file.name}`;
      const fileUrl = await uploadToStorage(filePath, file);

      let thumbUrl = fileUrl;
      let mediaItem = { isVideo, url: fileUrl, thumbUrl, filePath, thumbPath: null };

      if (isVideo) {
        generateVideoThumbnail(file, async (thumbBlob) => {
          const thumbPath = `posts/${postId}/thumb_${Date.now()}_${file.name}.jpg`;
          thumbUrl = await uploadToStorage(thumbPath, thumbBlob);
          mediaItem.thumbUrl = thumbUrl;
          mediaItem.thumbPath = thumbPath;
          setMediaContent((prev) => [...prev, mediaItem]);
          setLoading(false);
        });
      } else {
        const img = document.createElement("img");
        img.src = fileUrl;
        img.crossOrigin = "anonymous";
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const maxSize = 720;
          let width = img.width;
          let height = img.height;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(async (blob) => {
            const thumbPath = `posts/${postId}/thumb_${Date.now()}.jpg`;
            thumbUrl = await uploadToStorage(thumbPath, blob);
            mediaItem.thumbUrl = thumbUrl;
            mediaItem.thumbPath = thumbPath;
            setMediaContent((prev) => [...prev, mediaItem]);
            setLoading(false);
          }, "image/jpeg", 0.8);
        };
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleRemoveMedia = async (index) => {
    const media = mediaContent[index];
    try {
      if (media.filePath) {
        await deleteObject(ref(storage, media.filePath)).catch(() => { });
      }
      if (media.thumbPath) {
        await deleteObject(ref(storage, media.thumbPath)).catch(() => { });
      }
    } catch (err) {
      console.error("Lỗi xoá media:", err);
    }
    setMediaContent((prev) => prev.filter((_, idx) => idx !== index));
  };

  const createPost = async () => {
    if (!title.trim()) {
      alert("Tiêu đề là bắt buộc!");
      return;
    }

    try {
      setLoading(true);
      if (postId) {
        // Save data
        await updateDoc(doc(db, "posts", postId), {
          title,
          textContent,
          linkContent,
          mediaContent,
          editedDate: Date.now(),
          isDraft: false, // chuyển thành post chính thức
        });

        // Send notification
        sendNewPostNotification(postId);
      }
      alert("Tạo bài viết thành công!");
      onClose();
    } catch (err) {
      console.error(err);
      alert("Có lỗi khi tạo bài viết!");
    } finally {
      setLoading(false);
    }
  };

  // --- xoá draft + media nếu đóng popup mà chưa tạo post ---
  const handleClose = async () => {
    if (postId) {
      try {
        for (const m of mediaContent) {
          if (m.filePath) {
            await deleteObject(ref(storage, m.filePath)).catch(() => { });
          }
          if (m.thumbPath) {
            await deleteObject(ref(storage, m.thumbPath)).catch(() => { });
          }
        }
        await deleteDoc(doc(db, "posts", postId));
      } catch (err) {
        console.error("Lỗi khi xoá draft:", err);
      }
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-lg shadow-lg relative">
        <button
          className="absolute top-2 right-2 text-gray-600 hover:text-black"
          onClick={handleClose}
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-4">Tạo bài viết mới</h2>

        <input
          type="text"
          placeholder="Tiêu đề *"
          className="border p-2 rounded w-full mb-3"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Nội dung"
          className="border p-2 rounded w-full mb-3"
          value={textContent}
          onChange={(e) => setTextContent(e.target.value)}
        />

        <input
          type="text"
          placeholder="Link"
          className="border p-2 rounded w-full mb-3"
          value={linkContent}
          onChange={(e) => setLinkContent(e.target.value)}
        />

        <div className="mb-3">
          <h3 className="font-semibold mb-2">Media</h3>

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Nhập URL ảnh/video"
              className="border p-2 rounded w-full"
              id="mediaUrl"
            />
            <button
              className="bg-green-500 text-white px-2 rounded"
              onClick={() => {
                const url = document.getElementById("mediaUrl").value.trim();
                if (url) {
                  const isVideo =
                    url.match(/\.(mp4|webm|ogg|mov)$/) ||
                    url.includes("youtube") || url.includes(".mp4");
                  addMediaFromUrl(url, Boolean(isVideo));
                  document.getElementById("mediaUrl").value = "";
                }
              }}
            >
              Thêm URL
            </button>
          </div>

          <input
            type="file"
            accept="image/*,video/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const isVideo = file.type.startsWith("video");
                handleFileUpload(file, isVideo);
              }
            }}
          />

          <div className="mt-2 flex flex-wrap gap-2">
            {mediaContent.map((m, i) => (
              <div key={i} className="relative w-24 h-24">
                <img
                  src={m.thumbUrl}
                  alt=""
                  className="w-full h-full object-cover rounded"
                />
                {m.isVideo && (
                  <span className="absolute inset-0 flex items-center justify-center text-white bg-black bg-opacity-50">
                    🎥
                  </span>
                )}
                <button
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1"
                  onClick={() => handleRemoveMedia(i)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-3">
          <label className="flex items-center gap-2">
            <input
              id="publishCheckbox"
              type="checkbox"
              defaultChecked
              className="form-checkbox h-4 w-4"
              onChange={async (e) => {
                try {
                  if (!postId) return;
                  // nếu checked => disabled = false; nếu unchecked => disabled = true
                  await updateDoc(doc(db, "posts", postId), {
                    disabled: !e.target.checked,
                  });
                } catch (err) {
                  console.error("Lỗi cập nhật trạng thái công khai:", err);
                }
              }}
            />
            <span className="select-none">Đăng công khai</span>
          </label>
        </div>
        <button
          onClick={createPost}
          className="bg-vietnam-primary hover:bg-vietnam-dark text-white px-4 py-2 rounded w-full"
          disabled={loading}
        >
          {loading ? "Đang tạo..." : "Tạo bài viết"}
        </button>
      </div>
    </div>
  );
}
